/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Marcus Biesel <f.hartl@techdivision.com>
 */

import React, { useEffect } from 'react';
import { Step, StepLabel, Stepper } from '@mui/material';
import PropTypes from 'prop-types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import theme from '../../Config/ThemeConfig';
import { useCheckoutStore } from '../../Hooks/Zustand/useCheckoutStore';
import DatalayerService from '../../Services/Tracking/DatalayerService';
import { nextButtonActive } from '../../Signals/formStateSignal';

/**
 * FormStepper Component
 * @param isMobile
 * @param stepperData
 * @param activeStep
 * @param setActiveStep
 * @param lastValidStep
 * @param children
 * @returns {JSX.Element}
 * @constructor
 */
export function FormStepper({
  isMobile, stepperData, activeStep, setActiveStep, lastValidStep, children,
}) {
  const { t } = useTranslation();
  const { setActiveStep: setActiveStepInStore, getFormDataValue } = useCheckoutStore();

  useEffect(() => {
    // Push the step switch information to the Google datalayer
    DatalayerService.pushStepToDatalayer(activeStep);
    nextButtonActive.value = !(
      activeStep === 3
            && getFormDataValue('paymentChoice') === 'CREDIT_CARD'
            && getFormDataValue('adyenPaymentState') !== 'PAYMENT_COMPLETE'
    );
  }, [activeStep, getFormDataValue]);

  /**
     * Get step label style
     * @param index
     * @returns {string}
     */
  const getStepLabelStyle = (index) => {
    if (index <= lastValidStep) {
      if (activeStep !== 5) {
        return 'pointer';
      }
    }
    return 'not-allowed';
  };

  const renderDesktopStepper = () => (
    <Stepper
      nonLinear
      className="fo-stepper"
      activeStep={activeStep}
      alternativeLabel
      sx={{
        '& .MuiStepConnector-line': {
          borderTopWidth: '4px',
        },
        '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
          borderColor: 'primary.main',
        },
        '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
          borderColor: 'primary.main',
        },
      }}
    >
      {stepperData.map((step, index) => (
        <Step key={t(step.label)} completed={index < lastValidStep}>
          <StepLabel
            onClick={() => {
              if (activeStep !== 5) {
                setActiveStep(index);
                setActiveStepInStore(index);
              }
            }}
            style={{ cursor: getStepLabelStyle(index) }}
            StepIconProps={{
              icon:
                                index < lastValidStep ? (
                                  <CheckCircleIcon sx={{ fill: theme.palette.primary.main }} color="primary" />
                                ) : (
                                  index + 1
                                ),
            }}
          >
            {t(step.label)}
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  );

  return (
    <>
      {isMobile ? null : renderDesktopStepper()}
      {children}
    </>
  );
}

FormStepper.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  stepperData: PropTypes.array.isRequired,
  activeStep: PropTypes.number.isRequired,
  setActiveStep: PropTypes.func.isRequired,
  lastValidStep: PropTypes.number.isRequired,
  children: PropTypes.object.isRequired,
};
